import React from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import { Link } from 'gatsby'
import styled from 'styled-components'
import moment from 'moment'
import PreviewCompatibleImage from '../components/PreviewCompatibleImage'
import Content from '../components/Content'
import ButtonLink from '../components/ButtonLink'
import _ from 'lodash'

const Heading = styled(Link)`
  position: absolute;
  display: flex;
  left: 0;
  top: 1rem;
  align-items: center;
  width: 15rem;
  min-height: 14rem;
  background-color: white;
  text-decoration: none;
  z-index: 1;

  @media (max-width: 43rem) {
    position: relative;
    width: auto;
    min-height: auto;
    margin: 2rem 0;
  }
`

const Title = styled.h1`
  font-family: mr-eaves-modern;
  font-size: 1rem;
  text-transform: uppercase;
  text-align: center;
  line-height: 1.8;
  letter-spacing: 0.2rem;
  width: 100%;
  margin: 2.5rem;
  overflow: auto;
`

const PublishDateContainer = styled.div`
  position: absolute;
  top: -10px;
  font-style: italic;
`

const PublishDateDay = styled.div`
  position: absolute;
  font-size: 2.2rem;
  letter-spacing: 0.5rem;
  top: -1.3rem;
  left: -1rem;
`

const PublishDateYear = styled.div`
  position: absolute;
  font-size: 1.14rem;
  letter-spacing: 0.2rem;
  top: 1.6rem;
  left: 2rem;
`

const PublishDate = ({ children }) => {
  const m = moment(children)
  const year = m.format('YYYY')
  const month = m.format('M')
  const day = m.format('DD')
  return (
    <PublishDateContainer>
      <PublishDateDay>
        {month}.{day}
      </PublishDateDay>
      <PublishDateYear>{year}</PublishDateYear>
    </PublishDateContainer>
  )
}

const IntroWrapper = styled.section``

const FeaturedImageWrapper = styled.figure`
  max-width: 33rem;
`

const FeaturedImageCaption = styled.figcaption``

const Description = styled(ReactMarkdown)`
  margin: 0.7rem 0;
`

const BlogPostWrapper = styled.article`
  position: relative;
  max-width: 40rem;
  margin: 5rem auto;
  padding-left: 7rem;
  color: #3b4e57;

  p {
    line-height: 2;
  }

  p img {
    max-width: 33rem;
    width: 100%;
  }

  img {
    max-width: 100%;
  }

  @media (max-width: 43rem) {
    margin-top: 0;
    padding-left: 2rem;
    padding-right: 2rem;
    max-width: 35rem;
  }
`

const ContinueReadingSection = styled.section`
  text-align: center;
`

const BlogIntro = ({
  date,
  description,
  featuredImage,
  featuredImageAlt,
  featuredImageTitle,
  featuredImageCaption,
  slug,
  title,
}) => (
  <IntroWrapper>
    <Heading to={slug || '#'}>
      <Title>{title}</Title>
      <PublishDate>{date}</PublishDate>
    </Heading>
    {featuredImage ? (
      <FeaturedImageWrapper>
        <PreviewCompatibleImage
          imageInfo={{
            image: featuredImage,
            alt: featuredImageAlt,
            title: featuredImageTitle,
          }}
        />
        {featuredImageCaption && (
          <FeaturedImageCaption>
            <ReactMarkdown source={featuredImageCaption} />
          </FeaturedImageCaption>
        )}
      </FeaturedImageWrapper>
    ) : null}
    <Description source={description} />
  </IntroWrapper>
)

const BlogBody = ({ content, contentComponent }) => {
  const PostContent = contentComponent || Content
  return <PostContent content={content} />
}

const FooterWrapper = styled.section`
  border-top: 1px solid #3b4e57;
  color: #4c6169;
`

const BlogFooter = ({ author, featuredImage, href, tags, title }) => {
  let origin = ''
  if (typeof window !== `undefined`) {
    origin = window.location.origin
  }
  const pinterestUrl = `http://pinterest.com/pin/create/bookmarklet/?url=${href}&is_video=false&description=${title}&media=${origin +
    (featuredImage && featuredImage.publicURL)}`
  const facebookUrl = `http://www.facebook.com/share.php?u=${href}&title=${title}`
  const twitterUrl = `https://twitter.com/intent/tweet?text=${title} ${href} via @oneandonlypaper`

  return (
    <FooterWrapper>
      share this post! <a href={pinterestUrl}>pinterest</a>,{' '}
      <a href={facebookUrl}>facebook</a>, <a href={twitterUrl}>twitter</a>?
    </FooterWrapper>
  )
}

export const BlogPostTemplate = ({
  content,
  contentComponent,
  date,
  description,
  href,
  slug,
  tags,
  title,
  helmet,
  featuredImage,
  featuredImageAlt,
  featuredImageTitle,
  featuredImageCaption,
  author,
}) => {
  slug = _.replace(slug, /[0-9-]+_/, '')
  return (
    <BlogPostWrapper>
      {helmet || ''}
      <BlogIntro
        title={title}
        slug={slug}
        date={date}
        description={description}
        featuredImage={featuredImage}
        featuredImageAlt={featuredImageAlt}
        featuredImageTitle={featuredImageTitle}
        featuredImageCaption={featuredImageCaption}
      />
      {content && (
        <BlogBody content={content} contentComponent={contentComponent} />
      )}
      {content && (
        <BlogFooter
          author={author}
          featuredImage={featuredImage}
          href={href}
          tags={tags}
          title={title}
        />
      )}
      {!content && (
        <ContinueReadingSection>
          <ButtonLink to={slug}>Continue Reading</ButtonLink>
        </ContinueReadingSection>
      )}
    </BlogPostWrapper>
  )
}

BlogPostTemplate.propTypes = {
  content: PropTypes.node,
  contentComponent: PropTypes.func,
  description: PropTypes.string,
  title: PropTypes.string,
  helmet: PropTypes.object,
}

export default BlogPostTemplate
